import type { ReactElement } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { IRange } from "app/pages/sales-intelligence/types/range";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type FindIncomingTrafficFilter from "../../../models/IncomingTrafficFilter/IncomingTrafficFilter";
import type { IIncomingTrafficFilterDto } from "../../../types/leadsSearchFilters.dto";
import leadsSearchFiltersI18nKeys from "../../../utils/i18n";
import { formatNumber } from "../../../utils/numberFormatters";
import { RangeSelectWithClearField } from "../../RangeSelectWithClearField/RangeSelectWithClearField";
import RangeSlider from "../../RangeSlider/RangeSlider";
import { FilterContentContainer } from "../styles";

const IncomingTrafficFilter = ({
    filter,
    value,
    onFilterUpdate,
}: IFilterViewBaseProps<FindIncomingTrafficFilter>): ReactElement | null => {
    const { translate } = useTranslate();

    const handleClear = (key: keyof IIncomingTrafficFilterDto) => () => {
        const updated = { ...value };
        delete updated[key];
        onFilterUpdate(updated);
    };

    const handleChange = (key: keyof IIncomingTrafficFilterDto, range: Partial<IRange<number>>) => {
        onFilterUpdate({
            ...value,
            [key]: range,
        });
    };

    const handleSliderChange = (key: keyof IIncomingTrafficFilterDto, range: Partial<IRange<number>>) => {
        if (is.undefined(range.from) && is.undefined(range.to)) {
            handleClear(key)();
            return;
        }

        handleChange(key, range);
    };

    return (
        <FilterContentContainer>
            <RangeSelectWithClearField
                field="monthlyVisits"
                buckets={filter.options["monthlyVisits"]}
                formatter={formatNumber}
                value={value?.monthlyVisits}
                onHandleChange={handleChange}
                onHandleClear={handleClear}
            />
            <RangeSelectWithClearField
                field="avgMonthlyVisits"
                buckets={filter.options["avgMonthlyVisits"]}
                formatter={formatNumber}
                value={value?.avgMonthlyVisits}
                onHandleChange={handleChange}
                onHandleClear={handleClear}
            />
            <RangeSelectWithClearField
                field="uniqueVisitors"
                buckets={filter.options["uniqueVisitors"]}
                formatter={formatNumber}
                value={value?.uniqueVisitors}
                onHandleChange={handleChange}
                onHandleClear={handleClear}
            />
            <RangeSelectWithClearField
                field="totalPageViews"
                buckets={filter.options["totalPageViews"]}
                formatter={formatNumber}
                value={value?.totalPageViews}
                onHandleChange={handleChange}
                onHandleClear={handleClear}
            />
            <RangeSlider
                name="mobileWebVisitsShare"
                title={translate(leadsSearchFiltersI18nKeys.filter("mobileWebVisitsShare"))}
                options={filter.options["mobileWebVisitsShare"]}
                value={value?.mobileWebVisitsShare}
                onAfterChange={(range: Partial<IRange<number>>) =>
                    handleSliderChange("mobileWebVisitsShare", range)
                }
            />
        </FilterContentContainer>
    );
};

export default IncomingTrafficFilter;
